import React, { useEffect, useMemo, useState } from "react";
import { ConnectDragSource, useDrag } from "react-dnd";

import { Button } from "@/Components/ui/button";
import {
   Dialog,
   DialogContent,
   DialogHeader,
   DialogTitle,
} from "@/Components/ui/dialog";
import {
   FolderIcon,
   FolderTree,
   Home,
   Image as ImageIcon,
   Search,
} from "lucide-react";

import { useAdminStore } from "@/store/useAdminStore";
import { FolderType, ImageType } from "@/types/productTypes";
import { Input } from "@/Components/ui/input";
import { ScrollArea } from "@/Components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/Components/ui/tabs";
import Breadcrumbs from "@/Modules/Admin/ImageManager/BreadCrumbs";

interface ImagePickerModalProps {
   open: boolean;
   onClose: () => void;
   onSelect: (image: ImageType) => void;
   currentImage?: ImageType;
   nodeId?: number;
   itemName?: string;
   nodeName?: string;
}

const ImageCard = ({
   image,
   onSelect,
}: {
   image: ImageType;
   onSelect: (image: ImageType) => void;
}) => {
   const [{ isDragging }, drag] = useDrag(
      () => ({
         type: "IMAGE", // Match this with the IMAGE_TYPE constant in ImageNode
         item: { id: image.id },
         collect: (monitor) => ({
            isDragging: monitor.isDragging(),
         }),
      }),
      [image]
   );

   return (
      <div
         ref={(node) => drag(node) as unknown as ConnectDragSource}
         className={`
                group relative cursor-pointer rounded-lg overflow-hidden
                ${isDragging ? "opacity-50" : ""}
                hover:ring-2 hover:ring-blue-400
                transition-all
            `}
         onClick={() => onSelect(image)}
      >
         <img
            src={image.url}
            alt={image.title || "Image"}
            className="w-full h-32 object-cover"
            draggable={false}
         />
         <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="absolute bottom-2 left-2 right-2">
               <p className="text-white text-sm truncate">
                  {image.title || "Untitled"}
               </p>
            </div>
         </div>
      </div>
   );
};

const FolderItem = ({
   folder,
   onNavigate,
   isActive,
}: {
   folder: FolderType;
   onNavigate: (id: number) => void;
   isActive?: boolean;
}) => {
   return (
      <div
         className={`
                flex items-center p-2 rounded-md cursor-pointer
                ${isActive ? "bg-blue-100 text-blue-700" : "hover:bg-gray-100"}
            `}
         onClick={() => onNavigate(folder.id)}
      >
         <FolderIcon className="h-4 w-4 mr-2" />
         <span className="text-sm truncate">{folder.name}</span>
         {folder.children && folder.children.length > 0 && (
            <span className="ml-auto text-xs text-gray-500">
               {folder.children.length}
            </span>
         )}
      </div>
   );
};

// Add a FolderCard component to display folders in the grid
const FolderCard = ({
   folder,
   onNavigate,
}: {
   folder: FolderType;
   onNavigate: (folderId: number) => void;
}) => {
   return (
      <div
         className="group relative cursor-pointer rounded-lg overflow-hidden border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-all"
         onClick={() => onNavigate(folder.id)}
      >
         <div className="flex flex-col items-center justify-center p-4 h-32">
            <FolderIcon className="h-12 w-12 text-gray-400 mb-2" />
            <p className="text-sm font-medium text-center truncate w-full">
               {folder.name}
            </p>
         </div>
      </div>
   );
};

export const ImagePickerModal: React.FC<ImagePickerModalProps> = ({
   open,
   onClose,
   onSelect,
   itemName,
   nodeName,
}) => {
   const { allImages, folders, fetchImages, fetchFolders } = useAdminStore();
   const [allReadyFetched, setAllReadyFetched] = useState(false);
   const [isLoading, setIsLoading] = useState(false);

   // Use a global last folder state
   const [currentFolderId, setCurrentFolderId] = useState<number | null>(() => {
      const savedFolder = localStorage.getItem("lastImagePickerFolder");
      return savedFolder ? Number(savedFolder) : null;
   });

   const [searchTerm, setSearchTerm] = useState("");
   const [view, setView] = useState<"grid" | "list">("grid");
   const [isSearching, setIsSearching] = useState(false);

   // Save current folder when it changes
   useEffect(() => {
      if (currentFolderId !== null) {
         localStorage.setItem(
            "lastImagePickerFolder",
            currentFolderId.toString()
         );
      }
   }, [currentFolderId]);

   // Initial data fetch
   useEffect(() => {
      if (open && !allReadyFetched) {
         setIsLoading(true);
         Promise.all([fetchImages(), fetchFolders()])
            .then(() => {
               setAllReadyFetched(true);
            })
            .finally(() => {
               setIsLoading(false);
            });
      }
   }, [open, allReadyFetched, fetchImages, fetchFolders]);

   // Only reset search when modal closes
   useEffect(() => {
      if (!open) {
         setSearchTerm("");
         // Don't reset currentFolderId to maintain folder selection
      }
   }, [open]);

   // Load last folder on mount
   useEffect(() => {
      if (open) {
         const savedFolder = localStorage.getItem("lastImagePickerFolder");
         if (savedFolder) {
            setCurrentFolderId(Number(savedFolder));
         }
      }
   }, [open]);

   const recentImages = useMemo(() => {
      if (!allReadyFetched) return [];

      try {
         const recentIds: number[] = JSON.parse(
            localStorage.getItem("recentImages") || "[]"
         );
         return recentIds
            .map((id) => allImages.find((img) => img.id === id))
            .filter(Boolean)
            .slice(0, 6);
      } catch (error) {
         console.error("Error parsing recent images:", error);
         return [];
      }
   }, [allReadyFetched]);

   // Get child folders for the current folder
   const childFolders = useMemo(() => {
      return folders.filter((folder) => folder.parentId === currentFolderId);
   }, [folders, currentFolderId]);

   // Handle modal close
   const handleClose = () => {
      if (isLoading) return; // Prevent closing while loading
      onClose();
   };

   // Handle image selection
   const handleSelect = async (image: ImageType) => {
      try {
         setIsLoading(true);
         // Save to recent images
         const recentIds: number[] = JSON.parse(
            localStorage.getItem("recentImages") || "[]"
         );
         const newRecentIds = [
            image.id,
            ...recentIds.filter((id) => id !== image.id),
         ].slice(0, 6);
         localStorage.setItem("recentImages", JSON.stringify(newRecentIds));

         await onSelect(image);
      } finally {
         setIsLoading(false);
         handleClose();
      }
   };

   const currentFolderImages = useMemo(() => {
      if (searchTerm) {
         setIsSearching(true);
         // When searching, look through all images regardless of folder
         return allImages.filter(
            (image) =>
               image.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               image.description
                  ?.toLowerCase()
                  .includes(searchTerm.toLowerCase())
         );
      } else {
         setIsSearching(false);
         // When not searching, show images from current folder
         // If currentFolderId is null (root), show images with no folder
         return allImages.filter((image) =>
            currentFolderId === null
               ? image.folderId === null
               : image.folderId === currentFolderId
         );
      }
   }, [allImages, currentFolderId, searchTerm]);

   return (
      <Dialog open={open} onOpenChange={handleClose}>
         <DialogContent className="max-w-5xl h-[85vh] flex flex-col p-0">
            <DialogHeader className="px-6 py-4 border-b">
               <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                     <DialogTitle>
                        {isLoading ? "Loading..." : "Select Image"}
                     </DialogTitle>
                     {(nodeName || itemName) && (
                        <p className="text-sm text-gray-500 mt-1">
                           {nodeName && `Node: ${nodeName}`}
                           {nodeName && itemName && " • "}
                           {itemName && `Item: ${itemName}`}
                        </p>
                     )}
                  </div>
                  <div className="flex items-center space-x-2">
                     <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                           setView(view === "grid" ? "list" : "grid")
                        }
                     >
                        <ImageIcon className="h-4 w-4" />
                     </Button>
                  </div>
               </div>
               <div className="flex items-center space-x-4 mt-4">
                  <div className="relative flex-1">
                     <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                     <Input
                        placeholder="Search images..."
                        className="pl-8"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                     />
                  </div>
               </div>
            </DialogHeader>

            {/* Add loading overlay */}
            {isLoading && (
               <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-50">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
               </div>
            )}

            <div className="flex flex-1 min-h-0">
               <div className="w-64 border-r flex flex-col">
                  <Tabs defaultValue="folders" className="flex-1">
                     <TabsList className="w-full">
                        <TabsTrigger value="folders" className="flex-1">
                           <FolderTree className="h-4 w-4 mr-2" />
                           Folders
                        </TabsTrigger>
                     </TabsList>
                     <ScrollArea className="flex-1">
                        <div className="p-4 space-y-1">
                           <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start"
                              onClick={() => setCurrentFolderId(null)}
                           >
                              <Home className="h-4 w-4 mr-2" />
                              Root
                           </Button>
                           {folders
                              .filter(
                                 (folder) => folder.parentId === currentFolderId
                              )
                              .map((folder) => (
                                 <FolderItem
                                    key={folder.id}
                                    folder={folder}
                                    onNavigate={setCurrentFolderId}
                                    isActive={folder.id === currentFolderId}
                                 />
                              ))}
                        </div>
                     </ScrollArea>
                  </Tabs>
               </div>

               <div className="flex-1 flex flex-col">
                  {/* Breadcrumbs navigation - hide when searching */}
                  {!isSearching && (
                     <div className="px-4 py-2 border-b">
                        <Breadcrumbs
                           folders={folders}
                           currentFolderId={currentFolderId}
                           onNavigate={setCurrentFolderId}
                        />
                     </div>
                  )}

                  {/* Show search indicator when searching */}
                  {isSearching && searchTerm && (
                     <div className="px-4 py-2 border-b bg-blue-50">
                        <p className="text-sm text-blue-700">
                           Searching all images for "{searchTerm}" (
                           {currentFolderImages.length} results)
                        </p>
                     </div>
                  )}

                  {!currentFolderId && recentImages.length > 0 && (
                     <div className="p-4 border-b">
                        <h3 className="text-sm font-medium mb-3">
                           Recent Images
                        </h3>
                        <div className="grid grid-cols-6 gap-2">
                           {recentImages.map(
                              (image) =>
                                 image && (
                                    <div
                                       key={image.id}
                                       className="aspect-square rounded-md overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-400"
                                       onClick={() => handleSelect(image)}
                                    >
                                       <img
                                          src={image.url}
                                          alt={image.title || "Recent"}
                                          className="w-full h-full object-cover"
                                       />
                                    </div>
                                 )
                           )}
                        </div>
                     </div>
                  )}

                  <ScrollArea className="flex-1 p-4">
                     {isSearching ? (
                        currentFolderImages.length > 0 ? (
                           <div
                              className={
                                 view === "grid"
                                    ? "grid grid-cols-3 gap-4"
                                    : "space-y-2"
                              }
                           >
                              {currentFolderImages.map((image) => (
                                 <ImageCard
                                    key={image.id}
                                    image={image}
                                    onSelect={handleSelect}
                                 />
                              ))}
                           </div>
                        ) : (
                           <div className="flex flex-col items-center justify-center h-full text-gray-500 py-12">
                              <Search className="h-12 w-12 mb-4 text-gray-300" />
                              <p>No images found matching "{searchTerm}"</p>
                              <Button
                                 variant="ghost"
                                 size="sm"
                                 onClick={() => setSearchTerm("")}
                                 className="mt-2"
                              >
                                 Clear search
                              </Button>
                           </div>
                        )
                     ) : (
                        <>
                           {/* Display folders first */}
                           {childFolders.length > 0 && (
                              <div className="mb-6">
                                 <h3 className="text-sm font-medium mb-3">
                                    Folders
                                 </h3>
                                 <div
                                    className={
                                       view === "grid"
                                          ? "grid grid-cols-3 gap-4"
                                          : "space-y-2"
                                    }
                                 >
                                    {childFolders.map((folder) => (
                                       <FolderCard
                                          key={folder.id}
                                          folder={folder}
                                          onNavigate={setCurrentFolderId}
                                       />
                                    ))}
                                 </div>
                              </div>
                           )}

                           {/* Display images */}
                           {currentFolderImages.length > 0 && (
                              <div>
                                 <h3 className="text-sm font-medium mb-3">
                                    Images
                                 </h3>
                                 <div
                                    className={
                                       view === "grid"
                                          ? "grid grid-cols-3 gap-4"
                                          : "space-y-2"
                                    }
                                 >
                                    {currentFolderImages.map((image) => (
                                       <ImageCard
                                          key={image.id}
                                          image={image}
                                          onSelect={handleSelect}
                                       />
                                    ))}
                                 </div>
                              </div>
                           )}

                           {/* Empty state when no folders or images */}
                           {childFolders.length === 0 &&
                              currentFolderImages.length === 0 && (
                                 <div className="flex flex-col items-center justify-center h-full text-gray-500 py-12">
                                    <ImageIcon className="h-12 w-12 mb-4 text-gray-300" />
                                    <p>
                                       No content in{" "}
                                       {currentFolderId === null
                                          ? "root folder"
                                          : "this folder"}
                                    </p>
                                 </div>
                              )}
                        </>
                     )}
                  </ScrollArea>
               </div>
            </div>
         </DialogContent>
      </Dialog>
   );
};
